'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import type { Journey } from '../all-journeys/journeyData';

interface BookingWidgetProps {
  journey: Journey;
}

interface DepartureOption {
  id: string;
  date: string;
  label: string;
  spotsLeft: number;
  priceMultiplier: number;
  season: string;
}

const MONTH_MAP: Record<string, number> = {
  January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
  July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
};

function generateDepartures(journey: Journey): DepartureOption[] {
  const months = journey.months.length > 0 ? journey.months.slice(0, 4) : ['March', 'June', 'September', 'November'];
  const now = new Date();
  return months.map((m, i) => {
    const monthIdx = MONTH_MAP[m] ?? i;
    const year = monthIdx <= now.getMonth() ? now.getFullYear() + 1 : now.getFullYear();
    const d = new Date(year, monthIdx, 8 + i * 4);
    const spotsLeft = [2, 4, 6, 8][i % 4];
    const isPeak = [5, 6, 7, 11].includes(monthIdx);
    return {
      id: `w-dep-${i}`,
      date: d.toISOString().split('T')[0],
      label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      spotsLeft,
      priceMultiplier: isPeak ? 1.15 : 1.0,
      season: isPeak ? 'Peak' : 'Value',
    };
  });
}

export default function JourneyBookingWidget({ journey }: BookingWidgetProps) {
  const [departures] = useState<DepartureOption[]>(() => generateDepartures(journey));
  const [selectedDep, setSelectedDep] = useState<DepartureOption | null>(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const pricePerPerson = selectedDep
    ? Math.round(journey.price * selectedDep.priceMultiplier)
    : journey.price;
  const total = pricePerPerson * adults + Math.round(pricePerPerson * 0.7) * children;

  // Auto-select first departure
  useEffect(() => {
    if (departures.length > 0 && !selectedDep) {
      setSelectedDep(departures[0]);
    }
  }, [departures, selectedDep]);

  const checkoutUrl = `/journey-checkout?journey=${journey.slug}&adults=${adults}&children=${children}${selectedDep ? `&dep=${selectedDep.id}` : ''}`;

  return (
    <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl overflow-hidden sticky top-20">
      {/* Price Header */}
      <div className="p-5 border-b border-[var(--border)]">
        <div className="flex items-end justify-between mb-1">
          <div>
            <span className="text-2xl font-bold text-[var(--primary)]">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(pricePerPerson)}
            </span>
            <span className="text-xs text-[var(--muted-foreground)] ml-1">/ person</span>
          </div>
          {selectedDep && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              selectedDep.season === 'Peak' ? 'bg-amber-400/10 text-amber-400' : 'bg-emerald-400/10 text-emerald-400'
            }`}>
              {selectedDep.season} Season
            </span>
          )}
        </div>
        <p className="text-xs text-[var(--muted-foreground)]">per person, double occupancy</p>
      </div>

      <div className="p-5 space-y-4">
        {/* Departure Dates */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Select Departure</p>
          <div className="space-y-1.5">
            {departures.map((dep) => (
              <button
                key={dep.id}
                onClick={() => setSelectedDep(dep)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-xs transition-all flex items-center justify-between ${
                  selectedDep?.id === dep.id
                    ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.06)] text-[var(--foreground)]'
                    : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[rgba(201,168,76,0.3)]'
                }`}
              >
                <span className="font-medium">{dep.label}</span>
                <span className={`font-medium ${dep.spotsLeft <= 3 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {dep.spotsLeft <= 3 ? `⚡ ${dep.spotsLeft} left` : `${dep.spotsLeft} spots`}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Guest Counter */}
        <div>
          <button
            onClick={() => setExpanded((e) => !e)}
            className="w-full flex items-center justify-between text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            <span className="uppercase tracking-wider">
              {adults} Adult{adults > 1 ? 's' : ''}{children > 0 ? `, ${children} Child${children > 1 ? 'ren' : ''}` : ''}
            </span>
            <span className="text-[var(--primary)]">{expanded ? '▲' : '▼'}</span>
          </button>

          {expanded && (
            <div className="mt-3 space-y-3 bg-[var(--surface-3)] rounded-lg p-3">
              {/* Adults */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">Adults</p>
                  <p className="text-[10px] text-[var(--muted-foreground)]">Age 12+</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAdults((a) => Math.max(1, a - 1))}
                    className="w-7 h-7 rounded-full border border-[var(--border)] flex items-center justify-center text-sm hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                  >−</button>
                  <span className="w-5 text-center text-sm font-bold">{adults}</span>
                  <button
                    onClick={() => setAdults((a) => Math.min(20, a + 1))}
                    className="w-7 h-7 rounded-full border border-[var(--border)] flex items-center justify-center text-sm hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                  >+</button>
                </div>
              </div>
              {/* Children */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">Children</p>
                  <p className="text-[10px] text-[var(--muted-foreground)]">Age 2–11 (30% off)</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setChildren((c) => Math.max(0, c - 1))}
                    className="w-7 h-7 rounded-full border border-[var(--border)] flex items-center justify-center text-sm hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                  >−</button>
                  <span className="w-5 text-center text-sm font-bold">{children}</span>
                  <button
                    onClick={() => setChildren((c) => Math.min(10, c + 1))}
                    className="w-7 h-7 rounded-full border border-[var(--border)] flex items-center justify-center text-sm hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                  >+</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Price Summary */}
        <div className="bg-[var(--surface-3)] rounded-lg p-3 space-y-1.5 text-xs">
          <div className="flex justify-between text-[var(--muted-foreground)]">
            <span>{adults} adult{adults > 1 ? 's' : ''}</span>
            <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(pricePerPerson * adults)}</span>
          </div>
          {children > 0 && (
            <div className="flex justify-between text-[var(--muted-foreground)]">
              <span>{children} child{children > 1 ? 'ren' : ''}</span>
              <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.round(pricePerPerson * 0.7) * children)}</span>
            </div>
          )}
          <div className="pt-1.5 border-t border-[var(--border)] flex justify-between font-bold">
            <span>Total</span>
            <span className="text-[var(--primary)]">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(total)}
            </span>
          </div>
        </div>

        {/* CTA Buttons */}
        <Link
          href={checkoutUrl}
          className="block w-full py-3 text-center text-sm font-bold uppercase tracking-wider rounded-xl bg-[var(--primary)] text-black hover:opacity-90 transition-opacity"
        >
          Book This Journey
        </Link>
        <Link
          href={`/tailor-made?ref=${encodeURIComponent(journey.title)}`}
          className="block w-full py-2.5 text-center text-xs font-semibold uppercase tracking-wider rounded-xl border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all"
        >
          Tailor This Journey
        </Link>

        {/* Journey Stats */}
        <div className="space-y-1.5 text-xs text-[var(--muted-foreground)] pt-1 border-t border-[var(--border)]">
          <div className="flex justify-between">
            <span>Duration</span>
            <span className="font-medium text-[var(--foreground)]">{journey.days} days</span>
          </div>
          <div className="flex justify-between">
            <span>Group Size</span>
            <span className="font-medium text-[var(--foreground)]">{journey.groupSize}</span>
          </div>
          <div className="flex justify-between">
            <span>Activity Level</span>
            <span className="font-medium text-[var(--foreground)]">{journey.activityLevel}</span>
          </div>
          <div className="flex justify-between">
            <span>Availability</span>
            <span className={`font-medium ${journey.availability === 'Limited availability' ? 'text-amber-400' : 'text-emerald-400'}`}>
              {journey.availability}
            </span>
          </div>
        </div>

        {/* AI Concierge */}
        <div className="pt-1 border-t border-[var(--border)]">
          <p className="text-[10px] text-[var(--muted-foreground)] text-center">
            🤖 Ask our{' '}
            <Link href="/ai-concierge" className="text-[var(--primary)] hover:underline">AI Concierge</Link>
            {' '}about this journey
          </p>
        </div>
      </div>
    </div>
  );
}
